import { useStudyText } from '@/i18n/studyText';
import { useEffect, useState, type FC } from 'react';
import { studentApi } from '../api';
import type { StudentAssignment } from '../types';

interface StudentSubmissionModalProps {
  isOpen: boolean;
  assignment: StudentAssignment | null;
  onClose: () => void;
  onSubmit: (assignmentId: string, payload: { attachment_id?: string; client_request_id?: string; feedback_note?: string }) => Promise<void>;
}

export const StudentSubmissionModal: FC<StudentSubmissionModalProps> = ({
  isOpen,
  assignment,
  onClose,
  onSubmit,
}) => {
  const st = useStudyText();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [attachment, setAttachment] = useState<{ id: string } | null>(null);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) { setFile(null); setAttachment(null); setNote(''); setError(''); setRequestId(crypto.randomUUID()); }
  }, [isOpen, assignment?.id]);
  if (!isOpen || !assignment) return null;

  const currentVersion = assignment.my_submission?.version || 0;
  const nextVersion = currentVersion + 1;

  const handleSubmit = async () => {
    if (!file || isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    try {
      const uploaded = attachment || await studentApi.uploadCourseFile(assignment.discipline_code, file);
      setAttachment(uploaded);
      await onSubmit(assignment.id, {
        attachment_id: uploaded.id, client_request_id: requestId,
        feedback_note: note.trim() || undefined,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : st('Не удалось отправить работу'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
              {st("ЗАГРУЗИТЬ НОВУЮ ВЕРСИЮ")}</span>
            <h3 className="text-base font-bold text-white mt-0.5">{assignment.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            aria-label={st("Закрыть")}
          >
            ✕
          </button>
        </div>

        <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 space-y-1">
          <p>
            <span className="font-semibold text-slate-400">{st("Текущая версия:")}</span>
            {st("Версия")}{currentVersion} ({st(assignment.status)})
          </p>
          <p>
            <span className="font-semibold text-cyan-400">{st("Новая версия:")}</span>
            {st("Версия")}{nextVersion} {st("(будет отправлена на проверку)")}</p>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-slate-300 font-medium mb-1">{st("Файл работы (до 20 МБ)")}</label>
            <input
              aria-label={st("Файл работы")}
              type="file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setAttachment(null); setRequestId(crypto.randomUUID()); }}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white focus:border-cyan-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-slate-300 font-medium mb-1">
              {st("Комментарий для преподавателя (исправления, ссылки)")}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={st("Укажите, какие замечания были учтены в этой версии работы...")}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </div>

        {error && <p role="alert" className="text-red-400">{error}</p>}
        <div className="pt-3 border-t border-slate-800 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-700 hover:bg-slate-800 text-xs font-semibold text-slate-300"
          >
            {st("Отмена")}</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !file}
            className="px-5 py-2 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md disabled:opacity-50"
          >
            {isSubmitting ? st("Отправка...") : `${st('Отправить версию')} ${nextVersion}`}
          </button>
        </div>
      </div>
    </div>
  );
};
